import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// IMPORTANT — auth transport rationale:
// We use Bearer tokens (Authorization header) instead of httpOnly cookies because
// the Emergent ingress (Cloudflare) overrides CORS preflight responses with
// `Access-Control-Allow-Origin: *`, which browsers reject when `credentials: include`
// is set. As long as REACT_APP_BACKEND_URL points to a different subdomain than the
// frontend (cross-origin), cookie-based auth cannot work. The backend ALSO accepts
// the cookie path, so this is a frontend-only choice we can flip back later.
const TOKEN_KEY = "qfresh_token";

export const tokenStore = {
  get: () => {
    // localStorage throws in some private-browsing modes — null is the correct fallback.
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  },
  set: (t) => {
    try { localStorage.setItem(TOKEN_KEY, t); }
    catch (err) { console.warn("tokenStore.set failed (private mode?)", err); }
  },
  clear: () => {
    try { localStorage.removeItem(TOKEN_KEY); }
    catch (err) { console.warn("tokenStore.clear failed", err); }
  },
};

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const t = tokenStore.get();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      tokenStore.clear();
    }
    return Promise.reject(err);
  }
);

// ---- Auth ----
export const authApi = {
  register: (data) => api.post("/auth/register", data).then((r) => r.data),
  login: (data) => api.post("/auth/login", data).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  updateMe: (data) => api.put("/auth/me", data).then((r) => r.data),
  togglePremium: () => api.post("/auth/premium/toggle").then((r) => r.data),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, newPassword) =>
    api.post("/auth/reset-password", { token, new_password: newPassword }).then((r) => r.data),
};

// ---- Households ----
export const householdsApi = {
  create: (name) => api.post("/households", { name }).then((r) => r.data),
  join: (code) => api.post("/households/join", { code }).then((r) => r.data),
  switch: (householdId) =>
    api.post("/households/switch", { household_id: householdId }).then((r) => r.data),
  all: () => api.get("/households/all").then((r) => r.data),
  leave: () => api.post("/households/leave").then((r) => r.data),
  me: () => api.get("/households/me").then((r) => r.data),
  stats: () => api.get("/households/me/stats").then((r) => r.data),
  setMemberRole: (userId, role) =>
    api.patch(`/households/members/${userId}`, { role }).then((r) => r.data),
  removeMember: (userId) =>
    api.delete(`/households/members/${userId}`).then((r) => r.data),
  setIcon: (icon) =>
    api.patch("/households/icon", { icon }).then((r) => r.data),
};

// ---- Locations ----
export const locationsApi = {
  list: () => api.get("/locations").then((r) => r.data),
  create: (name) => api.post("/locations", { name }).then((r) => r.data),
  remove: (id) => api.delete(`/locations/${id}`).then((r) => r.data),
};

// ---- Containers ----
export const containersApi = {
  list: (params = {}) => api.get("/containers", { params }).then((r) => r.data),
  get: (id) => api.get(`/containers/${id}`).then((r) => r.data),
  byQr: (qr) => api.get(`/containers/by-qr/${qr}`).then((r) => r.data),
  create: (data) => api.post("/containers", data).then((r) => r.data),
  update: (id, data) => api.put(`/containers/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/containers/${id}`).then((r) => r.data),
  updateSharing: (id, data) =>
    api.patch(`/containers/${id}/sharing`, data).then((r) => r.data),
  boxes: () => api.get("/containers/boxes").then((r) => r.data),
  deleteBox: (boxNumber) =>
    api.delete(`/containers/by-box/${boxNumber}`).then((r) => r.data),
  deleteAllBoxes: () => api.delete("/containers/all").then((r) => r.data),
  setBoxColor: (boxNumber, color) =>
    api.patch(`/containers/by-box/${boxNumber}/color`, { color }).then((r) => r.data),
};

// ---- Stats / Smart fridge / Seed ----
export const statsApi = { get: () => api.get("/stats").then((r) => r.data) };

export const voiceApi = {
  parseContainer: (transcript) =>
    api.post("/voice/parse-container", { transcript }).then((r) => r.data),
};

export const foodApi = {
  estimateExpiry: (name, storageDate) =>
    api.post("/food/estimate-expiry", { name, storage_date: storageDate }).then((r) => r.data),
};
export const smartFridgeApi = { prompt: () => api.get("/smart-fridge/prompt").then((r) => r.data) };
export const notificationsApi = {
  feed: () => api.get("/notifications/feed").then((r) => r.data),
  testEmail: () => api.post("/notifications/test-email").then((r) => r.data),
  dismiss: (alertId) =>
    api.post("/notifications/dismiss", { alert_id: alertId }).then((r) => r.data),
  dismissAll: (alertIds) =>
    api.post("/notifications/dismiss-all", { alert_ids: alertIds }).then((r) => r.data),
  restore: (alertIds) =>
    api.post("/notifications/restore", { alert_ids: alertIds }).then((r) => r.data),
};
export const seedApi = { run: () => api.post("/seed").then((r) => r.data) };

// ---- Web Push ----
export const pushApi = {
  getVapidKey: () => api.get("/push/vapid-public-key").then((r) => r.data),
  status: () => api.get("/push/status").then((r) => r.data),
  subscribe: (payload) => api.post("/push/subscribe", payload).then((r) => r.data),
  unsubscribe: (payload) => api.post("/push/unsubscribe", payload).then((r) => r.data),
  test: () => api.post("/push/test").then((r) => r.data),
};

// ---- Billing / Stripe ----
export const billingApi = {
  packages: () => api.get("/billing/packages").then((r) => r.data),
  createCheckoutSession: (packageId, originUrl) =>
    api
      .post("/billing/create-checkout-session", {
        package_id: packageId,
        origin_url: originUrl,
      })
      .then((r) => r.data),
  status: (sessionId) =>
    api.get(`/billing/status/${sessionId}`).then((r) => r.data),
};

// ---- Admin (gated by ADMIN_EMAILS on the backend) ----
export const adminApi = {
  health: () => api.get("/admin/health").then((r) => r.data),
};

// Pretty-print FastAPI errors
export const formatError = (err) => {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
  if (detail?.msg) return detail.msg;
  return String(detail);
};
